/*
 * Ryan O'Dowd
 * 2022-05-20
 * © Copyright 2022 Oakwood Software Consulting, Inc. All Rights Reserved.
 */
import AnimatedRings from './AnimatedRings';
import Popover from '@components/common/Popover';
import React from 'react';
import Text from '@components/common/Text';
import {
  View,
} from 'react-native';
import {
  format as dateFnsFormat,
} from 'date-fns';
import getStyles from './styles';
import hooks from '@hooks';

const titleDateFormat = 'eeee, MMMM d'; // @TODO: globals?

const DailyRings = (_p: DailyRingsProps) => {
  const props = {
    ...defaultProps,
    ..._p,
  };

  const styles = hooks.useStyles(getStyles, {size: props.size});
  const i18n = hooks.useI18n();

  const numLessonsToday = hooks.useTypedSelector((state) => +state.numLessonsToday); // @TODO: make this not include verse tests...lessons only
  const user = hooks.useTypedSelector((state) => state.oakUser);
  const numPeanutsToday = hooks.useTypedSelector((state) => {
    const userSevenDaysPeanuts = state.peanutsSevenDays[user.id]?.peanuts;
    const ret = +(userSevenDaysPeanuts?.[userSevenDaysPeanuts.length - 1]?.peanuts || 0);
    return isNaN(ret) ? 0 : ret;
  });
  const numVersesLeveledUpToday = hooks.useTypedSelector((state) => {
    const todayVerses = state.peanutsSevenDays[user.id]?.passedVerses;
    const ret = +(todayVerses?.[todayVerses.length - 1]?.passed_verses || 0);
    return isNaN(ret) ? 0 : ret;
  });
  const dailyLessonsGoal = hooks.useTypedSelector((state) => +state.goalSettings.dailyLessons || 2); // @TODO: need a redux migration to avoid ||
  const dailyPeanutsGoal = hooks.useTypedSelector((state) => +state.goalSettings.dailyPeanuts || 15); // @TODO: need a redux migration to avoid ||
  const dailyVersesLeveledUpGoal = hooks.useTypedSelector((state) => +state.goalSettings.dailyVersesLeveledUp || 2); // @TODO: need a redux migration to avoid ||
  const streaks: any = hooks.useTypedSelector((state) => state.streaks);
  const currStreakNum = streaks.current_streak_num;
  const streakEndDate = new Date(streaks.current_streak_end_date);
  const now = new Date();
  const todaysStreakContinued = (streakEndDate.getUTCFullYear() === now.getFullYear()) && (streakEndDate.getUTCMonth() === now.getMonth()) && (streakEndDate.getUTCDate() === now.getDate());

  const versesLeveledUpFillPercent = Math.min(100, (props.versesLeveledUp || numVersesLeveledUpToday) / dailyVersesLeveledUpGoal * 100);
  const peanutsFillPercent = Math.min(100, (props.peanutsEarned || numPeanutsToday) / dailyPeanutsGoal * 100);
  const lessonsFillPercent = Math.min(100, (props.lessonsCompleted || numLessonsToday) / dailyLessonsGoal * 100);
  // @TODO: use this to make entire widget complete...should be done at dashboard level, not in this component... const allGoalsCompleted = versesLeveledUpFillPercent === 100 && peanutsFillPercent === 100 && lessonsFillPercent === 100;

  const scaledSize = styles.globalScale * props.size;
  const ringWidth = scaledSize / 16;

  return (
    <Popover.Wrapper
      popover={
        <Popover.Inner>
          <Popover.Text title={true}>{dateFnsFormat(new Date(), titleDateFormat, {locale: i18n.dateFnsLocale})}</Popover.Text>
          {/* @TODO: add graphic here */}
          <Popover.Text><Popover.Text style={styles.label}>{i18n('currentStreak')}: </Popover.Text>{currStreakNum}</Popover.Text>
          <Popover.Text><Popover.Text style={styles.label}>{i18n('peanutsEarned')}: </Popover.Text>{numPeanutsToday}/{dailyPeanutsGoal}</Popover.Text>
          <Popover.Text><Popover.Text style={styles.label}>{i18n('lessonsCompleted')}: </Popover.Text>{numLessonsToday}/{dailyLessonsGoal}</Popover.Text>
          <Popover.Text><Popover.Text style={styles.label}>{i18n('versesLeveledUp')}: </Popover.Text>{numVersesLeveledUpToday}/{dailyVersesLeveledUpGoal}</Popover.Text>
        </Popover.Inner>
      }
    >
      <View style={styles.dailyGoalRings}>
        <View style={styles.container}>
          <View style={styles.inner}>
            <AnimatedRings
              size={scaledSize}
              width={ringWidth}
              versesLeveledUpFillPercent={versesLeveledUpFillPercent}
              lessonsFillPercent={lessonsFillPercent}
              peanutsFillPercent={peanutsFillPercent}
            >
              {() => (
                <Text
                  style={[styles.streakText, (todaysStreakContinued && styles.streakActive)]}
                  numberOfLines={1}
                  adjustsFontSizeToFit={true}
                >{/* @TODO: we should probably look at the actual streak data instead of relying on number of lessons, but this is good for now based on the curent business logic (just be aware that in the future, there might be other ways to continue a streak other than doing a lesson) */}
                  {currStreakNum}
                </Text>
              )}
            </AnimatedRings>
          </View>
        </View>
      </View>
    </Popover.Wrapper>
  );
};

class DailyRingsProps {
  lessonsCompleted?: number;
  peanutsEarned?: number;
  versesLeveledUp?: number;
  dense?: boolean;
  size?: number = 72;
};
const defaultProps = new DailyRingsProps();

export default DailyRings;
